import * as React from "react";
import {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {useAxiosPrivate} from "../../hooks/useAxiosPrivate";
import {getAllEventsData, getEventTicketDetails} from "../../service/admin/eventApi";
import {FlyoutSuperUserNavigationMenu} from "../../components/admin/FlyoutSuperUserNavigationMenu";
import {t} from "i18next";
import {DownloadIcon} from "@radix-ui/react-icons";
import {Spinner} from "../../components/Spinner.tsx";
import {generateExcelFile} from "../../service/admin/fileApi";

export const Events = (props) => {
    const {selectedMenuOption, setSelectedMenuOption} = props;

    // Hooks
    const axiosPrivate = useAxiosPrivate();

    // Data
    const ticketListExcelHeader = {
        "firstName": "First Name",
        "middleName": "Middle Name",
        "modeOfAttending": "Mode of Attending",
        "numberOfSeats": "Number of Seats",
        "phoneNumber": "Phone Number",
        "emailId": "Email ID"
    };

    const [eventsDataLoading, setEventsDataLoading] = useState(false);
    const [eventsData, setEventsData] = useState([]);

    const [selectedEventData, setSelectedEventData] = useState({});
    const [selectedEventTicketDetailsLoading, setSelectedEventTicketDetailsLoading] = useState(false);
    const [selectedEventTicketDetails, setSelectedEventTicketDetails] = useState([]);

    // Export Button
    const [isExportAsExcelDisabled, setIsExportAsExcelDisabled] = useState(false);
    const [isExportAsExcelLoading, setIsExportAsExcelLoading] = useState(false);

    // Event Data Related Functions
    const getAllEvents = async () => {
        setEventsDataLoading(true);
        try {
            const response = await getAllEventsData(axiosPrivate);
            console.log("(SuperUser/Events.jsx) Get Registered Events Data Response: ", response);
            if (response.success) {
                setEventsData(response.data.sort((a, b) => new Date(b.startDate) - new Date(a.startDate)));
                setSelectedEventData(response.data[0]);
            } else {
                setEventsData([]);
            }
        } catch (error) {
            console.error("(Events.jsx) Error fetching Events data: ", error);
            toast.error("Something went wrong while fetching Events data!");
        } finally {
            setEventsDataLoading(false);
        }
    }

    const getSelectedEventTicketDetails = async () => {
        setSelectedEventTicketDetailsLoading(true);
        setSelectedEventTicketDetails([]);
        try {
            const response = await getEventTicketDetails(axiosPrivate, selectedEventData._id);
            console.log("(SuperUser/Events.jsx) Get All Events Data Response: ", response);

            if (response.success) {
                setSelectedEventTicketDetails(response.data);
            } else {
                setSelectedEventTicketDetails([]);
            }
        } catch (error) {
            console.error("(Events.jsx) Error fetching Logged-In Events data: ", error);
            toast.error("(Events.jsx) Something went wrong while fetching Logged-In Events data!");
        } finally {
            setSelectedEventTicketDetailsLoading(false);
        }
    }

    // Export as Excel Functions
    const exportAsExcel = async () => {
        try {
            setIsExportAsExcelLoading(true);
            setIsExportAsExcelDisabled(true);

            let excelData = {};

            if (selectedEventData) {
                if (selectedEventData.isSeatAllocationEnabled) {
                    // I want to add the Row Number column after the Number of Seats column
                    ticketListExcelHeader["rowNumber"] = "Row Number";

                    excelData = {
                        worksheetName: "tickets-list",
                        headers: ticketListExcelHeader,
                        data: selectedEventTicketDetails.map((ticket) => ({
                            firstName: ticket.userDetails.firstName,
                            middleName: ticket.userDetails.middleName,
                            modeOfAttending: ticket.modeOfAttending,
                            numberOfSeats: ticket.numberOfSeats,
                            rowNumber: ticket.rowNumber,
                            phoneNumber: `${ticket.userDetails.phone.dialingCode} ${ticket.userDetails.phone.number}`,
                            emailId: ticket.userDetails.email
                        }))
                    }
                } else {
                    excelData = {
                        worksheetName: "tickets-list",
                        headers: ticketListExcelHeader,
                        data: selectedEventTicketDetails.map((ticket) => ({
                            firstName: ticket.userDetails.firstName,
                            middleName: ticket.userDetails.middleName,
                            modeOfAttending: ticket.modeOfAttending,
                            numberOfSeats: ticket.numberOfSeats,
                            phoneNumber: `${ticket.userDetails.phone.dialingCode} ${ticket.userDetails.phone.number}`,
                            emailId: ticket.userDetails.email
                        }))
                    }
                }
            }

            const response = await generateExcelFile(axiosPrivate, excelData);
            console.log("(SuperUser/Events.jsx) Generate Excel File Response: ", response);

            if (response.success) {
                const blob = new Blob([response.data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});

                const url = window.URL.createObjectURL(blob);
                const downloadLink = document.createElement('a');
                downloadLink.href = url;
                // I want to file name to be in the format of "10th May 2025 - Event Name - Tickets List.xlsx"
                downloadLink.download = `${new Date(selectedEventData.startDate).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric"
                }).replace(/^(\d{1,2})/, d => d + (["th", "st", "nd", "rd"][(d % 10 > 3 || (d % 100 >= 11 && d % 100 <= 13)) ? 0 : d % 10]))
                } - ${selectedEventData.title} - Tickets List.xlsx`;

                // downloadLink.download = `tickets-list.xlsx`;
                downloadLink.click();
                downloadLink.remove();
                window.URL.revokeObjectURL(url);
            } else {
                toast.error("Something went wrong while exporting data!");
            }
        } catch (error) {
            console.error("(Events.jsx) Error exporting data: ", error);
            toast.error("Something went wrong while exporting data!");
        } finally {
            setIsExportAsExcelLoading(false);
            setIsExportAsExcelDisabled(false);
        }
    }

    useEffect(() => {
        console.log("(Events.jsx) Selected Event Data: ", selectedEventData);
        if (selectedEventData && selectedEventData._id) {
            console.log("Fetching Selected Event Ticket Details!");
            getSelectedEventTicketDetails();
        }
    }, [selectedEventData]);

    useEffect(() => {
        console.log("(Events.jsx) Events Component Mounted!");
        getAllEvents();

        return () => {
            console.log("(Events.jsx) Events Component Unmounted!");
        };
    }, []);

    return (
        <div
            className="flex flex-col items-start justify-between h-full overflow-hidden
                portrait:px-[1rem] portrait:py-[0.75rem]
                portrait:jk:px-[1.25rem] portrait:jk:py-[1rem]
                landscape:px-[1rem] landscape:py-[0.75rem]
                landscape:jk:px-[1rem] landscape:jk:py-[0.75rem]"
        >
            {/* Header | Action Buttons */}
            <div className={"w-full flex items-center gap-3 h-[20%]"}>
                {/* Navigation Button for Mobile */}
                <FlyoutSuperUserNavigationMenu
                    selectedMenuOption={selectedMenuOption}
                    setSelectedMenuOption={setSelectedMenuOption}
                />

                {/* Heading */}
                <h1
                    className="w-full flex-1 font-custom-bold leading-tight
                        portrait:text-[1.5rem] portrait:md:text-[1.375rem]
                        portrait:jk:text-[1.5rem] portrait:lg:text-[2rem
                        landscape:text-[0.65rem] landscape:md:text-[0.875rem]
                        landscape:jk:text-[1rem] landscape:lg:text-[1.5rem]"
                >
                    Events
                </h1>

                {/* Export as Excel */}
                <button
                    disabled={(isExportAsExcelDisabled || selectedEventTicketDetailsLoading || eventsDataLoading)}
                    className={`custom-button-w-trailing-icon h-full !flex-[0] 
                        portrait:aspect-square portrait:items-center portrait:!justify-center
                        ${isExportAsExcelDisabled ? 'bg-[#F2F2F2] cursor-default' : 'bg-white'}
                        transition-all duration-150 ease-in-out hover:shadow-lg hover:shadow-black/20`}
                    onClick={exportAsExcel}
                >
                    <div className={"portrait:!hidden"}>
                        <p className={"whitespace-nowrap !font-custom-semi-bold"}>
                            {`${t("SU_DASHBOARD.USERS.DOWNLOAD.LABEL")} ${t("SU_DASHBOARD.USERS.DOWNLOAD.OPTIONS.EXCEL")}`}
                        </p>
                    </div>
                    {
                        isExportAsExcelLoading
                            ? (
                                <Spinner
                                    visible={isExportAsExcelLoading}
                                    width="3vh"
                                    strokeWidth="5"
                                    strokeColor={"#FF6600"}
                                    animationDuration="4"
                                />
                            )
                            : (
                                <DownloadIcon
                                    aria-hidden="true"
                                    stroke={"#292D32"}
                                    strokeWidth={"0.1vh"}
                                />
                            )
                    }
                </button>
            </div>

            {/* Body | Events List | Content - Tickets List | Event Summary */}
            {
                eventsDataLoading
                    ? (
                        <div className={"h-full flex flex-col items-center justify-center w-full gap-1"}>
                            <Spinner
                                visible={eventsDataLoading}
                                width="2rem"
                                strokeWidth="5"
                                strokeColor={"#FF6600"}
                                animationDuration="1"
                            />
                        </div>
                    )
                    : (
                        <div className={"flex flex-col items-start justify-start w-full gap-1"}>
                            {/* Events List */}
                            <div className={`w-full flex items-center gap-2 overflow-auto thin-scrollbar`}>
                                {
                                    eventsData.length > 0 && (
                                        eventsData.map((event) => {
                                            return (
                                                <button
                                                    key={event._id}
                                                    className={`portrait:min-w-80 portrait:w-80 portrait:max-w-80
                                                        landscape:min-w-96 landscape:w-96 landscape:max-w-96
                                                        p-1 rounded-xl border overflow-hidden
                                                        ${selectedEventData._id === event._id ? "bg-[#ff964d]" : "bg-gray-300"}
                                                        font-custom-semi-bold
                                                        transition duration-500 ease-in-out`}
                                                    onClick={() => setSelectedEventData(event)}
                                                >
                                                    <img
                                                        className={`w-full aspect-auto rounded-lg`}
                                                        src={event.bannerUrl}
                                                        alt={event.title}
                                                    />
                                                </button>
                                            );
                                        })
                                    )
                                }
                            </div>

                            {/* Content - Tickets List */}
                            {
                                selectedEventTicketDetailsLoading
                                    ? (
                                        <div
                                            className={`w-full h-full flex-1 flex items-center justify-center`}
                                        >
                                            <Spinner
                                                visible={selectedEventTicketDetailsLoading}
                                                width="2rem"
                                                strokeWidth="5"
                                                strokeColor={"#FF6600"}
                                                animationDuration="1"
                                            />
                                        </div>
                                    )
                                    : (
                                        <div
                                            className={`w-full flex portrait:flex-col-reverse
                                                items-stretch gap-2 overflow-hidden h-[25%]`}
                                        >
                                            {/* Tickets List */}
                                            <div
                                                className={`relative flex-1 rounded-lg 
                                                    border border-gray-300 overflow-hidden`}
                                            >
                                                <div
                                                    className={`flex-1 w-full h-full
                                                    bg-white overflow-auto thin-scrollbar`}
                                                >
                                                    <table className={`w-full table-auto overflow-auto`}>
                                                        <thead className={"sticky top-0 bg-gray-100 rounded-t-lg"}>
                                                        <tr>
                                                            <th className={`p-2 font-custom-regular text-left whitespace-nowrap`}>
                                                                Photo
                                                            </th>
                                                            <th className={`p-2 font-custom-regular text-left whitespace-nowrap`}>
                                                                First Name
                                                            </th>
                                                            <th className={`p-2 font-custom-regular text-left whitespace-nowrap`}>
                                                                Middle Name
                                                            </th>
                                                            {
                                                                selectedEventData.requiresRegistration && (
                                                                    <>
                                                                        <th className={`p-2 font-custom-regular text-left whitespace-nowrap`}>
                                                                            Occupation Type
                                                                        </th>
                                                                        <th className={`p-2 font-custom-regular text-left whitespace-nowrap`}>
                                                                            Occupation Name
                                                                        </th>
                                                                    </>
                                                                )
                                                            }
                                                            {
                                                                selectedEventData.isVirtualModeEnabled && (
                                                                    <th className={`p-2 font-custom-regular text-left whitespace-nowrap`}>
                                                                        Mode of Attending
                                                                    </th>
                                                                )
                                                            }
                                                            {
                                                                selectedEventData.isTicketed && (
                                                                    <th className={`p-2 font-custom-regular text-left whitespace-nowrap`}>
                                                                        Number of Seats
                                                                    </th>
                                                                )
                                                            }
                                                            {
                                                                selectedEventData.isSeatAllocationEnabled && (
                                                                    <th className={`p-2 font-custom-regular text-left whitespace-nowrap`}>
                                                                        Row Number
                                                                    </th>
                                                                )
                                                            }
                                                            <th className={`p-2 font-custom-regular text-left whitespace-nowrap`}>
                                                                Phone Number
                                                            </th>
                                                            <th className={`p-2 font-custom-regular text-left whitespace-nowrap`}>
                                                                Email ID
                                                            </th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {
                                                            (eventsData.length > 0 && selectedEventTicketDetails.length > 0) ? (
                                                                selectedEventTicketDetails.map((ticket, index) => (
                                                                    <tr
                                                                        key={index}
                                                                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                                                                    >
                                                                        <td className="p-2 whitespace-nowrap">
                                                                            {ticket.userDetails.imageUrl ? (
                                                                                ticket.userDetails.imageUrl.length <= 2 ? (
                                                                                    <div
                                                                                        className={`z-10 h-[5vh] w-auto aspect-square rounded-full overflow-hidden 
                                                                                                flex items-center justify-center
                                                                                                ${ticket.userDetails.imageUrlBg}`}
                                                                                    >
                                                                                        <p className={"text-white font-custom-mono-bold"}>
                                                                                            {ticket.userDetails.imageUrl.toUpperCase()}
                                                                                        </p>
                                                                                    </div>
                                                                                ) : (
                                                                                    <img
                                                                                        className="h-[5vh] w-auto aspect-square rounded-full"
                                                                                        src={ticket.userDetails.imageUrl}
                                                                                        alt="User Profile Image"
                                                                                        referrerPolicy={"no-referrer"}
                                                                                    />
                                                                                )
                                                                            ) : (
                                                                                <img
                                                                                    className="h-[5vh] w-auto aspect-square rounded-full bg-[#D26A53]"
                                                                                    src={"/assets/default-pfp.png"}
                                                                                    alt="Default PFP"
                                                                                    referrerPolicy={"no-referrer"}
                                                                                />
                                                                            )}
                                                                        </td>
                                                                        <td className="p-2 whitespace-nowrap font-custom-regular">
                                                                            {ticket.userDetails.firstName}
                                                                        </td>
                                                                        <td className="p-2 whitespace-nowrap font-custom-regular">
                                                                            {ticket.userDetails.middleName}
                                                                        </td>
                                                                        {
                                                                            selectedEventData.requiresRegistration && (
                                                                                <>
                                                                                    <td className="p-2 whitespace-nowrap font-custom-regular">
                                                                                        {
                                                                                            ticket.occupationDetails?.type === "Other"
                                                                                                ? `Other | ${ticket.occupationDetails?.occupationTypeOtherName}`
                                                                                                : ticket.occupationDetails?.type
                                                                                        }
                                                                                    </td>
                                                                                    <td className="p-2 whitespace-nowrap font-custom-regular">
                                                                                        {ticket.occupationDetails?.name}
                                                                                    </td>
                                                                                </>
                                                                            )
                                                                        }
                                                                        {
                                                                            selectedEventData.isVirtualModeEnabled && (
                                                                                <td className="p-2 whitespace-nowrap font-custom-regular">
                                                                                    {ticket.modeOfAttending}
                                                                                </td>
                                                                            )
                                                                        }
                                                                        {
                                                                            selectedEventData.isTicketed && (
                                                                                <td className="p-2 whitespace-nowrap font-custom-regular">
                                                                                    {ticket.numberOfSeats ? ticket.numberOfSeats : ''}
                                                                                </td>
                                                                            )
                                                                        }
                                                                        {
                                                                            selectedEventData.isSeatAllocationEnabled && (
                                                                                <td className="p-2 whitespace-nowrap font-custom-regular">
                                                                                    {ticket.rowNumber ? ticket.rowNumber : ''}
                                                                                </td>
                                                                            )
                                                                        }
                                                                        <td className="p-2 whitespace-nowrap font-custom-regular">
                                                                            {/*{ticket.userDetails.phone.number ? `${ticket.userDetails.phone.dialingCode} ${ticket.userDetails.phone.number}` : ''}*/}
                                                                        </td>
                                                                        <td className="p-2 whitespace-nowrap font-custom-regular">
                                                                            {/*{ticket.userDetails.email ? ticket.userDetails.email : ''}*/}
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td
                                                                        colSpan="6"
                                                                        className="p-2 text-center text-gray-500 font-custom-medium-italic"
                                                                    >
                                                                        No data available
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>

                                            {/* Event Summary */}
                                            <div
                                                className={`landscape:h-full portrait:w-full
                                                flex landscape:flex-col items-start gap-2 
                                                overflow-auto thin-scrollbar`}
                                            >
                                                {/* Total Number of Registrations */}
                                                {
                                                    selectedEventData.requiresRegistration && (
                                                        <div
                                                            className={`flex items-start justify-between portrait:flex-1
                                                            min-w-60 w-60 max-w-60 landscape:p-[1rem] landscape:pt-[0.75rem] portrait:p-[0.75rem] 
                                                            rounded-xl border border-gray-300 outline-none overflow-hidden bg-white
                                                            font-custom-semi-bold transition duration-500 ease-in-out`}
                                                        >
                                                            <div className={`flex flex-col portrait:gap-1`}>
                                                                <h2 className={"font-custom-medium landscape:text-[1rem] text-gray-600"}>
                                                                    Total Registrations
                                                                </h2>
                                                                <p
                                                                    className={`font-custom-bold landscape:text-[1.5rem] 
                                                                    portrait:text-[2rem] portrait:leading-none text-[#292D32]`}
                                                                >
                                                                    {selectedEventData.numberOfRegistrations}
                                                                </p>
                                                            </div>
                                                            <div
                                                                className={`flex items-center justify-center
                                                                landscape:w-[2.5rem] portrait:w-[2.75rem] aspect-square 
                                                                border border-gray-300 rounded-full 
                                                                shadow-md shadow-black/10`}
                                                            >
                                                                <img
                                                                    className={`w-[75%] aspect-square`}
                                                                    src={`/assets/user-group-icon-coloured.svg`}
                                                                    alt={`Total Registrations`}
                                                                />
                                                            </div>
                                                        </div>
                                                    )
                                                }

                                                {/* Summary for Ticketed Events */}
                                                {
                                                    selectedEventData.isTicketed && (
                                                        <>
                                                            {/* Booked Seats */}
                                                            <div
                                                                className={`flex items-start justify-between portrait:flex-1
                                                                min-w-60 w-60 max-w-60 landscape:p-[1rem] landscape:pt-[0.75rem] portrait:p-[0.75rem] 
                                                                rounded-xl border border-gray-300 outline-none overflow-hidden bg-white
                                                                font-custom-semi-bold transition duration-500 ease-in-out`}
                                                            >
                                                                <div className={`flex flex-col portrait:gap-1`}>
                                                                    <h2 className={"font-custom-medium landscape:text-[1rem] text-gray-600"}>
                                                                        Booked Seats
                                                                    </h2>
                                                                    <p
                                                                        className={`font-custom-bold landscape:text-[1.5rem] 
                                                                        portrait:text-[2rem] portrait:leading-none text-[#292D32]`}
                                                                    >
                                                                        {selectedEventData.availableNumberOfTickets - selectedEventData.remainingNumberOfTickets}
                                                                    </p>
                                                                </div>
                                                                <div
                                                                    className={`flex items-center justify-center
                                                                    landscape:w-[2.5rem] portrait:w-[2.75rem] aspect-square 
                                                                    border border-gray-300 rounded-full 
                                                                    shadow-md shadow-black/10`}
                                                                >
                                                                    <img
                                                                        className={`w-[75%] aspect-square`}
                                                                        src={`/assets/booked-seats-icon.svg`}
                                                                        alt={`Booked Seats`}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Total Seats*/}
                                                            <div
                                                                className={`flex items-start justify-between portrait:flex-1
                                                                min-w-60 w-60 max-w-60 landscape:p-[1rem] landscape:pt-[0.75rem] portrait:p-[0.75rem] 
                                                                rounded-xl border border-gray-300 outline-none overflow-hidden bg-white
                                                                font-custom-semi-bold transition duration-500 ease-in-out`}
                                                            >
                                                                <div className={`flex flex-col portrait:gap-1`}>
                                                                    <h2 className={"font-custom-medium landscape:text-[1rem] text-gray-600"}>
                                                                        Total Seats
                                                                    </h2>
                                                                    <p
                                                                        className={`font-custom-bold landscape:text-[1.5rem] 
                                                                        portrait:text-[2rem] portrait:leading-none text-[#292D32]`}
                                                                    >
                                                                        {selectedEventData.availableNumberOfTickets}
                                                                    </p>
                                                                </div>
                                                                <div
                                                                    className={`flex items-center justify-center
                                                                    landscape:w-[2.5rem] portrait:w-[2.75rem] aspect-square 
                                                                    border border-gray-300 rounded-full 
                                                                    shadow-md shadow-black/10`}
                                                                >
                                                                    <img
                                                                        className={`w-[75%] aspect-square`}
                                                                        src={`/assets/total-seats-icon.svg`}
                                                                        alt={`Total Seats`}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Available Seats */}
                                                            <div
                                                                className={`flex items-start justify-between portrait:flex-1
                                                                min-w-60 w-60 max-w-60 landscape:p-[1rem] landscape:pt-[0.75rem] portrait:p-[0.75rem] 
                                                                rounded-xl border border-gray-300 outline-none overflow-hidden bg-white
                                                                font-custom-semi-bold transition duration-500 ease-in-out`}
                                                            >
                                                                <div className={`flex flex-col portrait:gap-1`}>
                                                                    <h2 className={"font-custom-medium landscape:text-[1rem] text-gray-600"}>
                                                                        Available Seats
                                                                    </h2>
                                                                    <p
                                                                        className={`font-custom-bold landscape:text-[1.5rem] 
                                                                        portrait:text-[2rem] portrait:leading-none text-[#292D32]`}
                                                                    >
                                                                        {selectedEventData.remainingNumberOfTickets}
                                                                    </p>
                                                                </div>
                                                                <div
                                                                    className={`flex items-center justify-center
                                                                    landscape:w-[2.5rem] portrait:w-[2.75rem] aspect-square 
                                                                    border border-gray-300 rounded-full 
                                                                    shadow-md shadow-black/10`}
                                                                >
                                                                    <img
                                                                        className={`w-[75%] aspect-square`}
                                                                        src={`/assets/available-seats-icon.svg`}
                                                                        alt={`Available Seats`}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    )
                            }
                        </div>
                    )
            }

            {/*/!* Footer - Pagination *!/*/}
            {/*<div>*/}
            {/*    <h1 className="text-2xl font-custom-bold">*/}
            {/*        Events*/}
            {/*    </h1>*/}
            {/*</div>*/}
        </div>
    );
}
